import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Check, X, Upload } from "lucide-react";
import { useProfiles } from "../context/ProfileContext";
import Avatar from "../components/Avatar";
import { toast } from "sonner";

const PALETTE = ["#ff3b1d", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#14b8a6"];

export default function ProfileSelect() {
  const { profiles, loading, selectProfile, createProfile, updateProfile, deleteProfile, uploadAvatar } = useProfiles();
  const navigate = useNavigate();
  const [mode, setMode] = useState("select"); // select | manage
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PALETTE[0]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState(PALETTE[0]);
  const fileRefs = useRef({});

  const choose = (p) => {
    selectProfile(p);
    toast.success(`Profil ${p.name} activé`);
    navigate("/");
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createProfile(newName.trim(), newColor);
      setAdding(false);
      setNewName("");
      toast.success("Profil créé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditName(p.name);
    setEditColor(p.avatar_color);
  };

  const saveEdit = async () => {
    try {
      await updateProfile(editingId, { name: editName.trim(), avatar_color: editColor });
      setEditingId(null);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const removeProfile = async (p) => {
    if (!window.confirm(`Supprimer le profil "${p.name}" ?`)) return;
    try {
      await deleteProfile(p.id);
      toast.success("Profil supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onAvatarPick = async (p, file) => {
    if (!file) return;
    try {
      await uploadAvatar(p.id, file);
      toast.success("Image de profil mise à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Chargement…</div>;
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 flex flex-col items-center">
      <div className="text-center mb-14 space-y-3">
        <div className="font-mono-mini text-[11px] uppercase tracking-[0.5em] text-[color:var(--ns-accent)]">
          Choix du profil
        </div>
        <h1 className="font-display text-4xl sm:text-6xl uppercase">
          Qui regarde&nbsp;?
        </h1>
        <button
          data-testid="profile-toggle-manage"
          onClick={() => setMode(mode === "manage" ? "select" : "manage")}
          className="mt-4 text-xs font-mono-mini uppercase tracking-widest text-zinc-400 hover:text-white transition"
        >
          {mode === "manage" ? "Terminer" : "Gérer les profils"}
        </button>
      </div>

      <div className="flex flex-wrap items-start justify-center gap-8 sm:gap-10 max-w-5xl">
        {profiles.map((p) => (
          <div key={p.id} data-testid={`profile-tile-${p.id}`} className="flex flex-col items-center gap-3 w-32">
            {editingId === p.id ? (
              <div className="flex flex-col items-center gap-3">
                <Avatar profile={{ ...p, name: editName, avatar_color: editColor }} size={96} />
                <input
                  data-testid={`profile-edit-name-${p.id}`}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-2 py-1 text-sm bg-black/40 border border-white/10 rounded text-center"
                />
                <div className="flex gap-1 flex-wrap justify-center">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditColor(c)}
                      className={`w-5 h-5 rounded-full ${editColor === c ? "ring-2 ring-white" : ""}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`profile-save-${p.id}`} onClick={saveEdit} className="w-8 h-8 rounded-full btn-accent flex items-center justify-center"><Check size={14} /></button>
                  <button onClick={() => setEditingId(null)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><X size={14} /></button>
                </div>
              </div>
            ) : (
              <button
                data-testid={`profile-pick-${p.id}`}
                onClick={() => (mode === "manage" ? startEdit(p) : choose(p))}
                className="group relative"
              >
                <div className="transition-transform group-hover:scale-105">
                  <Avatar profile={p} size={96} />
                </div>
                {mode === "manage" && (
                  <span className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition">
                    <Pencil size={18} />
                  </span>
                )}
              </button>
            )}

            {editingId !== p.id && (
              <>
                <div className="font-mono-mini text-xs uppercase tracking-widest text-zinc-300 text-center truncate w-full">
                  {p.name}
                </div>
                {mode === "manage" && (
                  <div className="flex gap-2">
                    <input
                      ref={(el) => (fileRefs.current[p.id] = el)}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onAvatarPick(p, e.target.files?.[0])}
                    />
                    <button
                      data-testid={`profile-upload-${p.id}`}
                      onClick={() => fileRefs.current[p.id]?.click()}
                      className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                      title="Changer l'image"
                    >
                      <Upload size={13} />
                    </button>
                    {profiles.length > 1 && (
                      <button
                        data-testid={`profile-delete-${p.id}`}
                        onClick={() => removeProfile(p)}
                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-[color:var(--ns-accent)] flex items-center justify-center"
                        title="Supprimer"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {profiles.length < 5 && (
          adding ? (
            <form onSubmit={submitNew} className="flex flex-col items-center gap-3 w-36">
              <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: newColor }}>
                <span className="font-display text-4xl text-white uppercase">
                  {(newName || "?").charAt(0)}
                </span>
              </div>
              <input
                data-testid="profile-new-name"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nom"
                className="w-full px-2 py-1 text-sm bg-black/40 border border-white/10 rounded text-center"
              />
              <div className="flex gap-1 flex-wrap justify-center">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    className={`w-5 h-5 rounded-full ${newColor === c ? "ring-2 ring-white" : ""}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button data-testid="profile-new-submit" type="submit" className="w-8 h-8 rounded-full btn-accent flex items-center justify-center"><Check size={14} /></button>
                <button type="button" onClick={() => setAdding(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><X size={14} /></button>
              </div>
            </form>
          ) : (
            <button
              data-testid="profile-add-btn"
              onClick={() => setAdding(true)}
              className="flex flex-col items-center gap-3 w-32"
            >
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-white/20 hover:border-[color:var(--ns-accent)] hover:text-[color:var(--ns-accent)] flex items-center justify-center text-zinc-500 transition">
                <Plus size={28} />
              </div>
              <div className="font-mono-mini text-xs uppercase tracking-widest text-zinc-400">
                Ajouter
              </div>
            </button>
          )
        )}
      </div>
    </div>
  );
}
